import React, { useContext, useEffect } from 'react';
import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';

import { calcEventState } from 'app/shared/utils/calcEventState';
import { useVisaAuth } from 'app/shared/utils/visa';
import { AuthContext } from 'app/shared/context/Auth';
import { useCityContext } from 'app/shared/context/City';
import { NotFound } from 'app/shared/components/pages/Status';
import { neighborhoodOrSecretLocation } from 'app/fan/utils';
import { addEventStructuredData } from 'app/fan/utils/eventHelper';
import { isEventIndexable } from 'app/fan/utils/eventHelper';
import { useEventPageContext } from 'app/fan/context/EventPage';
import { EventPageLoadingBlocks } from 'app/fan/components/pages/Event/components';
import {
  hasEventEnded,
  shouldRedirectToVisaOfferPage,
} from 'app/fan/components/pages/Event/eventUtils';

import { ApplyFlowPostInviteEventPage } from './ApplyFlowPostInviteEventPage';
import { ApplyFlowPreInviteEventPage } from './ApplyFlowPreInviteEventPage';
import { ApplyFlowTicketsUnavailableEventPage } from './ApplyFlowTicketsUnavailableEventPage';
import { BuyFlowAddToAccountEventPage } from './BuyFlowAddToAccountEventPage';
import { BuyFlowGuestPostPurchaseEventPage } from './BuyFlowGuestPostPurchaseEventPage';
import { BuyFlowLoggedInPostPurchaseEventPage } from './BuyFlowLoggedInPostPurchaseEventPage';
import { BuyFlowPrePurchaseEventPage } from './BuyFlowPrePurchaseEventPage';
import { BuyFlowTicketsUnavailableEventPage } from './BuyFlowTicketsUnavailableEventPage';
import { CompetitionFlowPreWinEventPage } from './CompetitionFlowPreWinEventPage';
import { CompetitionWinnerEventPage } from './CompetitionWinnerEventPage';
import { DoorsOpenPostShowEventPage } from './DoorsOpenPostShowEventPage';
import { PlusOneEventPage } from './PlusOneEventPage';

export const EventPageRouter = () => {
  const navigate = useNavigate();
  const { formatMessage } = useIntl();
  const {
    event,
    eventLoading,
    eventLoadError,
    eventAttendee,
    eventAttendeeLoading,
    forceStartedEventState,
    isPlusOneFlow,
    isAddToAccountFlow,
    isGuestAndHasTickets,
  } = useEventPageContext();
  const { user: loggedInUser } = useContext(AuthContext);
  const { setLastViewedCity } = useCityContext();

  const { visaPreSaleEligible, canBypassVisaValidation } = useVisaAuth({
    skipLoadCardRedemptionsStatus: true,
  });

  useEffect(() => {
    if (!eventLoadError && !eventLoading && event && isEventIndexable(event)) {
      const venueLocation = neighborhoodOrSecretLocation(event, formatMessage);
      addEventStructuredData({ event, venueLocation });
    }
  }, [eventLoadError, eventLoading, event]);

  useEffect(() => {
    if (!eventLoading && event && setLastViewedCity) {
      setLastViewedCity(event.city);
    }
  }, [eventLoading, event, setLastViewedCity]);

  if (eventLoadError) {
    return <NotFound noLayout={true} />;
  }

  if (!event || eventLoading || eventAttendeeLoading) {
    return <EventPageLoadingBlocks />;
  }

  const currentDate = new Date().getTime();
  const startsAtTime = new Date(event.startsAt).getTime();
  const eventStarted = startsAtTime - currentDate < 0;
  const eventEnded = hasEventEnded(event.startsAt, event.endsAt);

  if (forceStartedEventState || eventStarted || eventEnded) {
    return <DoorsOpenPostShowEventPage />;
  }

  if (event.isSpecialEvent && !!event.specialEventUrl) {
    window.location.href = event.specialEventUrl;
    return <EventPageLoadingBlocks />;
  }

  if (isPlusOneFlow && !!eventAttendee) {
    return <PlusOneEventPage />;
  }

  const isCompetitionEvent = !!event.competition?.id;
  const isApplyFlowEvent = event.attendeeFlow === 'apply';

  const attendeeState =
    !!loggedInUser && !eventAttendee?.visibleToUser
      ? undefined
      : eventAttendee?.currentState;
  const eventState = calcEventState({ event, attendeeState });

  // This is somewhat redundant but please keep it - it helps narrow down which `eventState`s are still possible later in the file
  const hasApplied =
    eventState === 'applied' ||
    (eventState !== 'cancelled' && attendeeState === 'applied');
  const canApplyOrHasApplied = eventState === 'apply' || hasApplied;
  // Make sure this stays BEFORE the isUnavailable check! For this file 'applied' needs to take precedence over 'soldout' and 'paused'!
  if (canApplyOrHasApplied) {
    return isCompetitionEvent ? (
      <CompetitionFlowPreWinEventPage />
    ) : (
      <ApplyFlowPreInviteEventPage />
    );
  }

  const isUnavailable =
    eventState === 'paused' ||
    eventState === 'soldout' ||
    eventState === 'cancelled';
  if (isUnavailable) {
    if (isApplyFlowEvent || isCompetitionEvent) {
      return <ApplyFlowTicketsUnavailableEventPage />;
    }
    return <BuyFlowTicketsUnavailableEventPage />;
  }

  if (isAddToAccountFlow) {
    return <BuyFlowAddToAccountEventPage />;
  }

  const needsVisaRedirect =
    !!event &&
    shouldRedirectToVisaOfferPage({
      event,
      eventAttendee,
      isPlusOneFlow,
      visaPreSaleEligible,
      canBypassVisaValidation,
    });
  if (needsVisaRedirect) {
    navigate('/visaoffer', { replace: false });
  }

  if (eventState === 'reserved' && !!loggedInUser) {
    return <BuyFlowLoggedInPostPurchaseEventPage />;
  }

  if (eventState === 'reserved' || isGuestAndHasTickets) {
    return <BuyFlowGuestPostPurchaseEventPage />;
  }

  if (eventState === 'buy') {
    return <BuyFlowPrePurchaseEventPage />;
  }

  if (isCompetitionEvent) {
    return <CompetitionWinnerEventPage />;
  }

  return <ApplyFlowPostInviteEventPage />;
};
